import { FunctionComponent, useRef, useState } from "react";

import { InputField } from "@/components/InputField/InputField";

import { InstructionHistoryDropdown } from "./InstructionHistoryDropdown";
import { filterInstructions } from "./useInstructionHistory";

/**
 * Row 1 of the GenerateBar: the "Tell AI what to write" instruction input.
 * Extracted from {@link GenerateBar} to keep that file under the length gate.
 * `compact` (the narrow DetailView "Chat" tab) forces a visible input border —
 * the default `sm:border-none` look relies on a shadow that reads as borderless
 * in the slim panel.
 *
 * The language control lives in {@link GenerateBarControlRow} (Row 2) since
 * PREC-3570. The input offers an autocomplete dropdown of previously used
 * instructions (`history`, persisted in localStorage by `useInstructionHistory`).
 */
export const GenerateBarInstructionRow: FunctionComponent<{
	isGenerating: boolean;
	userInstruction: string;
	history: string[];
	compact?: boolean;
	portalContainer?: HTMLElement | null;
	onChangeInstruction: (value: string) => void;
}> = ({
	isGenerating,
	userInstruction,
	history,
	compact,
	portalContainer,
	onChangeInstruction,
}) => {
	const anchorRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	const suggestions = filterInstructions(history, userInstruction);
	const showDropdown = isOpen && suggestions.length > 0;

	const closeDropdown = () => {
		setIsOpen(false);
		setHighlightedIndex(-1);
	};

	const selectSuggestion = (value: string) => {
		onChangeInstruction(value);
		closeDropdown();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (!showDropdown) {
			return;
		}

		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
		} else if (e.key === "Enter" && highlightedIndex >= 0) {
			// Only hijack Enter when a suggestion is highlighted; otherwise let the
			// textarea insert a newline as usual.
			e.preventDefault();
			selectSuggestion(suggestions[highlightedIndex]);
		} else if (e.key === "Escape") {
			closeDropdown();
		}
	};

	return (
		<div ref={anchorRef} className="w-full min-w-0 overflow-hidden">
			<InputField
				// Tailwind v4 important is a suffix (`border-solid!`); restores the border the
				// default `sm:border-none` removes, so the field reads as bordered in the slim panel.
				className={compact ? "border-solid!" : undefined}
				classNameWrapper="!mb-0"
				handleChange={(e) => {
					onChangeInstruction(e.target.value);
					setIsOpen(true);
					setHighlightedIndex(-1);
				}}
				informationPortalContainer={portalContainer}
				isDisabled={isGenerating}
				label="Tell AI what to write"
				classNameLabel="!text-[10px] !font-semibold !text-text-meta !tracking-wider !uppercase !whitespace-nowrap"
				information="AI will generate a full outreach sequence based on the lead's profile, signals, and your copywriting settings."
				multiline
				name="userInstruction"
				placeholder="Describe what to write..."
				value={userInstruction}
				onClick={() => setIsOpen(true)}
				onKeyDown={handleKeyDown}
			/>
			{showDropdown && (
				<InstructionHistoryDropdown
					anchorRef={anchorRef}
					highlightedIndex={highlightedIndex}
					portalContainer={portalContainer}
					suggestions={suggestions}
					onClose={closeDropdown}
					onSelect={selectSuggestion}
				/>
			)}
		</div>
	);
};
